import { useEffect, useRef, useState } from "react";
import { Activity } from "../../../@types/activities.ts";
import { Activitystream } from "../../../api/activitystreams.ts";
import { deliverPdf, ShareActivationLostError } from "./pdf/deliverPdf.ts";

export type PdfState = "idle" | "generating" | "readyToShare" | "error";

export type GenerateResult =
  | "delivered"
  | "cancelled"
  | "readyToShare"
  | "commentsFailed"
  | "error";

export type ActivityPdf = {
  state: PdfState;
  // The comments could not be loaded: the PDF step then offers the document
  // without them instead of quietly shipping a partial list
  commentsFailed: boolean;
  generate: (withComments: boolean) => Promise<GenerateResult>;
  // Delivers the file kept after a ShareActivationLostError on a fresh click
  shareReadyFile: () => Promise<GenerateResult>;
  // Forgets a pending file and any error (e.g. when the PDF step closes);
  // a generation still running is dropped once it finishes
  reset: () => void;
};

// What "comments" means for the PDF: the replies, and for a question the
// answers as well. The stream item carries only some of them, so the counters
// come first.
export function commentCount(activity: Activity) {
  const object = activity.object;
  const replies = object.reply_count ?? object.replies?.length ?? 0;
  if (object.type !== "question") return replies;
  return replies + (object.answer_count ?? object.answers?.length ?? 0);
}

// Generates the PDF of an activity and hands it to the user (download or
// share sheet). Lives on the activity, not in a menu or modal, so a run
// started from the share menu survives the menu closing.
export function useActivityPdf({
  activity,
  streams,
}: {
  activity: Activity;
  streams: Activitystream[];
}): ActivityPdf {
  const [state, setState] = useState<PdfState>("idle");
  const [commentsFailed, setCommentsFailed] = useState(false);
  const file = useRef<File | null>(null);
  // Bumped by reset(): a run that started before no longer touches the state
  const run = useRef(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const current = (id: number) => mounted.current && run.current === id;

  // `freshClick`: the click on "PDF teilen" after a lost share activation
  async function deliver(
    id: number,
    pdf: File,
    freshClick = false,
  ): Promise<GenerateResult> {
    try {
      const result = await deliverPdf(pdf, {
        activationMayHaveExpired: !freshClick,
      });
      if (!current(id)) return "cancelled";
      file.current = null;
      setState("idle");
      return result === "cancelled" ? "cancelled" : "delivered";
    } catch (error) {
      if (!current(id)) return "cancelled";
      // navigator.share() needs a fresh user activation once generating took
      // too long: keep the file and let the next click deliver it
      if (error instanceof ShareActivationLostError) {
        setState("readyToShare");
        return "readyToShare";
      }
      setState("error");
      return "error";
    }
  }

  async function generate(withComments: boolean): Promise<GenerateResult> {
    const id = ++run.current;
    file.current = null;
    setState("generating");
    setCommentsFailed(false);
    try {
      // Lazy: keeps the PDF libraries out of the main bundle
      const { generateActivityPdf, CommentsUnavailableError } =
        await import("./pdf/index.ts");
      try {
        const pdf = await generateActivityPdf(activity, {
          streams,
          includeComments: withComments,
        });
        if (!current(id)) {
          // Gone because the activity left the page, not because somebody
          // started over: say so, the document was on its way
          if (!mounted.current && run.current === id)
            toastr.error(I18n.t("js.activitystreams.share_dialog.pdf_lost"));
          return "cancelled";
        }
        file.current = pdf;
        return await deliver(id, pdf);
      } catch (error) {
        if (!(error instanceof CommentsUnavailableError)) throw error;
        // The document would claim more comments than it carries, so leave
        // the choice to try again or to take it without them
        if (!current(id)) return "cancelled";
        setCommentsFailed(true);
        setState("idle");
        return "commentsFailed";
      }
    } catch (error) {
      console.error("Generating the activity PDF failed", error);
      if (!current(id)) return "cancelled";
      setState("error");
      return "error";
    }
  }

  async function shareReadyFile(): Promise<GenerateResult> {
    if (!file.current) return "cancelled";
    return deliver(run.current, file.current, true);
  }

  function reset() {
    run.current++;
    file.current = null;
    setCommentsFailed(false);
    setState("idle");
  }

  return { state, commentsFailed, generate, shareReadyFile, reset };
}
